import { AVATARS, PLATFORM_PRESETS, RATIO_CSS } from "@easyshot/shared";
import { AvatarPortrait } from "@/components/avatar-portrait";
import { AvatarVideo } from "@/components/avatar-video";
import { SiteFooter, SiteNav } from "@/components/site-chrome";
import { Badge, Card, LinkButton, SectionTitle } from "@/components/ui";
import { HeroInput } from "@/components/hero-input";
import { TemplateShowcase } from "@/components/template-showcase";
import { LocalMoney } from "@/components/currency-provider";

const FEATURES: {
  icon: string;
  title: string;
  desc: string;
  soon?: boolean;
}[] = [
  {
    icon: "🔗",
    title: "URL to Video",
    desc: "Paste an Amazon, Shopify or TikTok Shop link. We pull the title, selling points and images — first script in 30 seconds.",
  },
  {
    icon: "🧍",
    title: "AI Avatar Presenters",
    desc: "6 studio-quality avatars with natural voices — new avatars added every month.",
  },
  {
    icon: "📦",
    title: "Product Showcase Videos",
    desc: "Static listing photos become moving product shots with camera moves, lighting and transitions.",
    soon: true,
  },
  {
    icon: "⚡",
    title: "Batch × 50",
    desc: "Generate 50 hook and avatar variations in one run — enough creatives to feed your A/B tests.",
  },
  {
    icon: "🎯",
    title: "Ad Cloner",
    desc: "Drop in a winning competitor ad. We break down its structure and pacing, then map it onto your product.",
    soon: true,
  },
  {
    icon: "📐",
    title: "Every Platform, One Export",
    desc: "TikTok, Reels, Shorts, Amazon, Shopee, Lazada — sizes and durations auto-adapted to each platform's rules.",
  },
  {
    icon: "🎬",
    title: "Scene Editor",
    desc: "Edit the hook, narration, captions and timing per scene before anything renders.",
  },
  {
    icon: "📊",
    title: "Performance Loop",
    desc: "Connect your ad accounts. Watch-through and ROI flow back to improve the next batch of scripts.",
    soon: true,
  },
];

const STEPS = [
  {
    n: "01",
    title: "Drop in a product link",
    desc: "Or paste a plain description. We extract the title, price, images and 4 core selling points.",
  },
  {
    n: "02",
    title: "Pick a style and avatar",
    desc: "UGC, faceless, unboxing, comparison review or cinematic — the script's tone follows the style.",
  },
  {
    n: "03",
    title: "Batch generate, then launch",
    desc: "Multiple platforms and variants in one run. Download the keepers, tweak scenes and re-render the rest.",
  },
];

const PRICING = [
  {
    name: "Pay as you go",
    priceCents: 190,
    unit: "/video",
    desc: "No subscription and no monthly fee. Add funds and pay in USD.",
    features: [
      { cents: 190, suffix: " welcome balance on signup" },
      "Pay only for what you render",
      "All avatars + all languages",
      "No watermark, 1080P",
      "All platform sizes included",
    ],
    cta: "Start free",
    highlight: false,
  },
  {
    name: "Pay as you go Batch",
    priceCents: 120,
    unit: "/video",
    desc: "Volume pricing for teams that test at scale",
    features: [
      "Everything in Pay as you go",
      { cents: 160, suffix: "/video from 50 videos" },
      { cents: 120, suffix: "/video from 500 videos" },
      "Batch up to 50 variants per run",
      "Built for hook & avatar A/B testing",
    ],
    cta: "Add funds",
    highlight: true,
  },
  {
    name: "Enterprise",
    price: "Custom",
    unit: "",
    desc: "For agencies and high-volume teams",
    features: [
      "Negotiated per-video rate",
      "Custom avatar cloning",
      "Team seats & sub-accounts",
      "API access",
      "Dedicated success manager",
    ],
    cta: "Contact us",
    highlight: false,
  },
];

export default function Home() {
  return (
    <>
      <SiteNav />

      {/* Hero */}
      <section className="mx-auto w-full max-w-6xl px-5 pt-3 pb-20 sm:pt-3">
        <div className="mx-auto max-w-3xl text-center">
          <h1 className="text-[2.65rem] leading-[1.06] font-semibold tracking-[-0.035em] sm:text-[4.25rem]">
            A full set of video ads{" "}
            <span className="text-[0.485em] font-normal tracking-normal text-muted">
              ... and more.
            </span>
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-[15px] leading-7 text-muted sm:text-base">
            EasyShot reads your product, writes the script, casts an AI
            presenter and cuts the scenes — delivering ready-to-run creatives
            for TikTok, Reels, Shorts, Amazon, Shopee and Lazada in one go.
          </p>
        </div>

        <HeroInput />

        <TemplateShowcase />

        <div className="mt-14 grid gap-4 sm:grid-cols-3">
          {[
            ["30 sec", "from link to first script"],
            ["50", "variants per batch run"],
            ["6", "platforms, one export"],
          ].map(([k, v]) => (
            <Card key={v} className="px-6 py-5 text-center">
              <div className="text-2xl font-semibold tracking-tight">{k}</div>
              <div className="mt-1 text-[13px] text-muted">{v}</div>
            </Card>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="mx-auto w-full max-w-6xl px-5 py-20">
        <SectionTitle
          eyebrow="How it works"
          title="Three steps from product to publish"
          desc="No editing skills, no scriptwriting. If you can copy a link, you can ship an ad."
        />
        <div className="mt-12 grid gap-5 md:grid-cols-3">
          {STEPS.map((s) => (
            <Card key={s.n} className="relative overflow-hidden p-7">
              <div className="mb-5 font-mono text-4xl font-semibold text-white/12">
                {s.n}
              </div>
              <h3 className="text-lg font-semibold">{s.title}</h3>
              <p className="mt-2.5 text-sm leading-6 text-muted">{s.desc}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* Features */}
      <section id="features" className="mx-auto w-full max-w-6xl px-5 py-20">
        <SectionTitle
          eyebrow="Feature matrix"
          title="Everything your ad pipeline needs"
          desc="From creative production to multi-platform delivery — one pipeline, no juggling five tools."
        />
        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {FEATURES.map((f) => (
            <Card
              key={f.title}
              className="p-6 transition hover:border-[var(--border-strong)] hover:bg-white/[0.06]"
            >
              <div className="mb-4 flex items-start justify-between">
                <div className="grid size-10 place-items-center rounded-xl bg-white/8 text-lg">
                  {f.icon}
                </div>
                {f.soon && <Badge>Coming soon</Badge>}
              </div>
              <h3 className="text-[15px] font-semibold">{f.title}</h3>
              <p className="mt-2 text-[13px] leading-6 text-muted">{f.desc}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* Avatars */}
      <section className="mx-auto w-full max-w-6xl px-5 py-20">
        <SectionTitle
          eyebrow="AI avatars"
          title="Different personas convert differently"
          desc="Every avatar is tagged with the styles and languages it fits — pick wrong and the script's tone will feel off."
        />
        <div className="mt-12 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          {AVATARS.map((a) => (
            <Card key={a.id} className="overflow-hidden p-0">
              <AvatarVideo avatarId={a.id} className="aspect-[3/4] w-full" />
              <div className="p-3.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[13px] font-medium">{a.name}</span>
                  {a.premium && (
                    <Badge tone="warning" className="px-1.5 py-0.5">
                      PRO
                    </Badge>
                  )}
                </div>
                <div className="mt-1 text-[11px] text-muted">
                  {a.locale} · {a.ageRange}
                </div>
              </div>
            </Card>
          ))}
        </div>
      </section>

      {/* Platform ratios */}
      <section className="mx-auto w-full max-w-6xl px-5 py-20">
        <SectionTitle
          eyebrow="Multi-platform"
          title="Generate once, publish everywhere"
          desc="Sizes, durations and safe zones are auto-cropped per platform — no exporting five times by hand."
        />
        <div className="mt-12 flex flex-wrap items-end justify-center gap-6">
          {PLATFORM_PRESETS.filter((p) => p.id !== "taobao").map((p) => (
            <div key={p.id} className="text-center">
              <div
                className="w-28 rounded-xl border border-line-strong bg-[linear-gradient(135deg,#ffffff1a,#ffffff05)] shadow-lg sm:w-32"
                style={{ aspectRatio: RATIO_CSS[p.ratio] }}
              />
              <div className="mt-3 text-[13px] font-medium">{p.label}</div>
              <div className="text-[11px] text-muted">
                {p.ratio} · {p.duration[0]}-{p.duration[1]}s
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Mobile app */}
      <section id="app" className="mx-auto w-full max-w-6xl px-5 py-20">
        <Card className="grid items-center gap-10 overflow-hidden p-8 sm:p-12 lg:grid-cols-2">
          <div>
            <Badge tone="accent" className="mb-5">
              📱 iOS / Android
            </Badge>
            <h2 className="text-3xl font-semibold tracking-tight">
              Watch your renders from your pocket
            </h2>
            <p className="mt-4 text-[15px] leading-7 text-muted">
              The EasyShot app runs the full URL-to-video flow: paste a link on
              your phone, pick a style and avatar, and watch renders land in the
              same project library as the web app.
            </p>
            <ul className="mt-6 space-y-3">
              {[
                "Full create flow, built for one thumb",
                "Live render progress on the go",
                "Download finished videos to your phone",
                "Same project library as the web app",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2.5 text-sm">
                  <span className="mt-0.5 text-[var(--success)]">✓</span>
                  <span className="text-muted">{t}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <span className="inline-flex cursor-default items-center rounded-full border border-line px-4 py-2 text-sm text-muted">
                 App Store — coming soon
              </span>
              <span className="inline-flex cursor-default items-center rounded-full border border-line px-4 py-2 text-sm text-muted">
                ▲ Google Play — coming soon
              </span>
            </div>
          </div>

          {/* 手机外框 */}
          <div className="flex justify-center">
            <div className="w-[248px] rounded-[2.2rem] border border-line-strong bg-[#0c0c14] p-2.5 shadow-2xl">
              <div className="overflow-hidden rounded-[1.7rem] bg-[#08080c]">
                <div className="flex items-center justify-between px-4 pt-3 pb-2 text-[10px] text-muted">
                  <span>9:41</span>
                  <span>●●● ▮</span>
                </div>
                <div className="px-4 pb-5">
                  <div className="text-[15px] font-semibold">Create</div>
                  <div className="mt-3 rounded-xl border border-line bg-white/4 px-3 py-2.5 text-[11px] text-white/35">
                    Paste a product link…
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    {AVATARS.slice(0, 4).map((a) => (
                      <AvatarPortrait
                        key={a.id}
                        avatarId={a.id}
                        className="aspect-[3/4] w-full rounded-lg"
                      />
                    ))}
                  </div>
                  <div className="mt-3 rounded-full bg-[linear-gradient(90deg,var(--accent),var(--accent-2))] py-2 text-center text-[11px] font-medium text-white">
                    Generate video
                  </div>
                  <div className="mt-4 space-y-2">
                    {[86, 42].map((v) => (
                      <div
                        key={v}
                        className="rounded-lg border border-line bg-white/3 p-2.5"
                      >
                        <div className="mb-1.5 flex justify-between text-[10px] text-muted">
                          <span>Rendering</span>
                          <span>{v}%</span>
                        </div>
                        <div className="h-1 rounded-full bg-white/8">
                          <div
                            className="h-full rounded-full bg-[var(--accent)]"
                            style={{ width: `${v}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </section>

      {/* Pricing */}
      <section id="pricing" className="mx-auto w-full max-w-6xl px-5 py-20">
        <SectionTitle
          eyebrow="Pricing"
          title="Pay as you go — no subscription"
          desc="Add funds to your USD balance and pay the displayed cash price for each render. Volume pricing applies automatically."
        />
        <div className="mt-12 grid gap-5 lg:grid-cols-3">
          {PRICING.map((p) => (
            <Card
              key={p.name}
              className={
                p.highlight
                  ? "relative border-[var(--accent)]/50 bg-gradient-to-b from-[var(--accent)]/12 to-transparent p-8"
                  : "p-8"
              }
            >
              {p.highlight && (
                <Badge
                  tone="accent"
                  className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[var(--accent)] text-white"
                >
                  Most popular
                </Badge>
              )}
              <div className="text-sm font-medium">{p.name}</div>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="text-4xl font-semibold tracking-tight">
                  {"priceCents" in p && p.priceCents ? (
                    <LocalMoney usdCents={p.priceCents} prefix="From " />
                  ) : (
                    p.price
                  )}
                </span>
                <span className="text-sm text-muted">{p.unit}</span>
              </div>
              <p className="mt-2 text-[13px] text-muted">{p.desc}</p>
              <ul className="my-7 space-y-3">
                {p.features.map((f) => (
                  <li
                    key={typeof f === "string" ? f : `${f.cents}-${f.suffix}`}
                    className="flex items-start gap-2.5 text-[13px]"
                  >
                    <span className="mt-0.5 text-[var(--success)]">✓</span>
                    <span className="text-muted">
                      {typeof f === "string" ? (
                        f
                      ) : (
                        <LocalMoney usdCents={f.cents} suffix={f.suffix} />
                      )}
                    </span>
                  </li>
                ))}
              </ul>
              <LinkButton
                href={
                  p.cta === "Add funds"
                    ? "/studio/billing"
                    : p.cta === "Contact us"
                      ? "mailto:hello@easyshot.ai"
                      : "/studio"
                }
                variant={p.highlight ? "primary" : "secondary"}
                className="w-full"
              >
                {p.cta}
              </LinkButton>
            </Card>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto w-full max-w-6xl px-5 py-20">
        <Card className="relative overflow-hidden px-6 py-16 text-center">
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                "radial-gradient(40rem 20rem at 50% 0%, #7c5cff33, transparent 70%)",
            }}
          />
          <div className="relative">
            <h2 className="text-3xl font-semibold tracking-tight sm:text-4xl">
              Try one right now
            </h2>
            <p className="mx-auto mt-4 max-w-md text-[15px] text-muted">
              No sign-up needed — paste a product link and see the script and
              storyboard.
            </p>
            <LinkButton href="/studio" size="lg" className="mt-8">
              Generate your first video free
            </LinkButton>
          </div>
        </Card>
      </section>

      <SiteFooter />
    </>
  );
}
